import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Send, Check, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter() {
  const { t, language } = useLanguage();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | duplicate | error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setStatus('loading');
    try {
      await axios.post(`${API}/newsletter`, { email, language });
      setStatus('success');
      setMessage(t('newsletter.success'));
      setEmail('');
      setTimeout(() => setStatus('idle'), 4000);
    } catch (err) {
      if (err.response?.status === 409) {
        setStatus('duplicate');
        setMessage(t('newsletter.duplicate'));
      } else {
        setStatus('error');
        setMessage('Something went wrong. Try again.');
      }
      setTimeout(() => setStatus('idle'), 4000);
    }
  };

  return (
    <section className="py-24 sm:py-32 bg-[#003d33] relative overflow-hidden" data-testid="newsletter-section">
      {/* Decorative accents */}
      <div className="absolute top-0 left-0 w-72 h-72 rounded-full bg-[#E31C25]/10 blur-[120px]" />
      <div className="absolute bottom-0 right-0 w-72 h-72 rounded-full bg-[#00897b]/15 blur-[120px]" />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="max-w-2xl mx-auto text-center">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#008751] block mb-4">
            {t('newsletter.label')}
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold text-white mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="newsletter-title"
          >
            {t('newsletter.title')}
          </h2>
          <p className="text-base lg:text-lg leading-relaxed text-zinc-400 mb-10">
            {t('newsletter.subtitle')}
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="newsletter-form">
            <Input
              type="email"
              placeholder={t('newsletter.placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-12 rounded-full bg-white/10 border-white/15 text-white placeholder:text-white/40 focus:border-white/30 focus:ring-white/20 flex-1"
              data-testid="newsletter-email-input"
            />
            <Button
              type="submit"
              disabled={status === 'loading'}
              className="h-12 px-8 rounded-full bg-white text-black hover:bg-zinc-200 font-semibold flex items-center gap-2 transition-transform hover:-translate-y-0.5 hover:shadow-lg"
              data-testid="newsletter-submit-btn"
            >
              {status === 'loading' ? (
                <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : status === 'success' ? (
                <Check className="w-4 h-4" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {t('newsletter.button')}
            </Button>
          </form>

          {/* Status Message */}
          {status !== 'idle' && status !== 'loading' && (
            <div
              className={`mt-4 flex items-center justify-center gap-2 text-sm ${
                status === 'success' ? 'text-green-400' : status === 'duplicate' ? 'text-yellow-400' : 'text-red-400'
              }`}
              data-testid="newsletter-status-message"
            >
              {status === 'success' ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              {message}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
